import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';

import { useEventTracker } from '../../hooks';

// Track component instances and script loading status
const LOADING_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  LOADED: 'loaded',
};

// Global variables for script management
const currentLocale = { current: null as string | null };
const loadingStatus = { current: LOADING_STATUS.IDLE };
const scriptLoadTimestamp = { current: 0 };
const isTrackingSetup = { current: false };
const cleanup = { current: undefined as (() => void) | undefined };

// Setup plugin step tracking
const setupStepTracking = ({
  trackCustomEvent,
}: {
  trackCustomEvent: (event: string, data: Record<string, unknown>) => void;
}) => {
  // Prevent setting up tracking multiple times
  if (!window.PexipEngage?.Plugin || isTrackingSetup.current) {
    return undefined;
  }

  // Mark tracking as set up
  isTrackingSetup.current = true;

  const handleStepShown = (event: CustomEvent) => {
    const stepData = event.detail.payload;

    trackCustomEvent('appointment_booking_service_interacted', {
      bookingEngine: 'Skedify',
      stepLabel: stepData.step,
      appointmentSubject: stepData?.subject?.title,
      appointmentStore: stepData.office?.title,
    });
  };

  // Store the event and handler for safe cleanup
  const eventName = window.PexipEngage.Plugin.EVENT_STEP_SHOWN;
  const eventHandler = handleStepShown as EventListener;

  window.PexipEngage.Plugin.addEventListener(eventName, eventHandler);

  return () => {
    isTrackingSetup.current = false;
  };
};

// Safely remove all Pexip scripts
const removeExistingScripts = () => {
  const existingScripts = document.querySelectorAll('script[src*="pexip-engage-plugin.js"]');

  existingScripts.forEach(script => script.remove());
};

// Handle cleanup of the Pexip plugin
const cleanupPexipPlugin = () => {
  if (cleanup.current) {
    try {
      cleanup.current();
    } catch (error) {
      console.error('Failed to clean up Pexip plugin:', error);
    }
    cleanup.current = undefined;
  }

  // Reset status
  loadingStatus.current = LOADING_STATUS.IDLE;
  isTrackingSetup.current = false;
};

// Manage script loading and plugin initialization
const loadPexipScript = (locale: string, onPluginLoaded: () => void) => {
  // Check if we're already loading the script (debounce multiple calls)
  if (loadingStatus.current === LOADING_STATUS.LOADING) {
    return;
  }

  // Don't reload if already loaded for this locale in last 500ms (prevents double loads)
  const now = Date.now();

  if (
    loadingStatus.current === LOADING_STATUS.LOADED &&
    currentLocale.current === locale &&
    now - scriptLoadTimestamp.current < 1000
  ) {
    return;
  }

  // Clean up previous instance if one exists
  cleanupPexipPlugin();

  // Set loading status
  loadingStatus.current = LOADING_STATUS.LOADING;

  // Update current locale
  currentLocale.current = locale;

  // Remove any existing scripts
  removeExistingScripts();

  const script = document.createElement('script');
  const skedifyProjectUrl = process.env.NEXT_PUBLIC_SKEDIFY_PROJECT_HOST || 'colora.plugin.skedify.io';

  script.src = `https://${skedifyProjectUrl}/colora/pexip-engage-plugin.js`;
  script.async = true;

  script.onload = () => {
    if (window.PexipEngage && window.PexipEngage.Plugin) {
      loadingStatus.current = LOADING_STATUS.LOADED;
      scriptLoadTimestamp.current = Date.now();

      window.PexipEngage.Plugin.awaitFirstInstance().then(onPluginLoaded);
    } else {
      // Reset if the script didn't load properly
      loadingStatus.current = LOADING_STATUS.IDLE;
    }
  };

  script.onerror = () => {
    // Reset status on error
    loadingStatus.current = LOADING_STATUS.IDLE;
  };

  document.body.appendChild(script);
};

const SkedifyColorAdvice = () => {
  const { locale } = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const { trackCustomEvent } = useEventTracker();
  const mountedRef = useRef(false);

  useEffect(() => {
    // Prevent double setup when strict mode causes double mount
    if (mountedRef.current) {
      const initializePlugin = () => {
        if (!isTrackingSetup.current) {
          cleanup.current = setupStepTracking({ trackCustomEvent });
        }
      };

      // Load script whenever locale changes
      loadPexipScript(locale || 'nl-BE', initializePlugin);
    }

    mountedRef.current = true;

    // Cleanup on unmount or locale change
    return () => {
      // No automatic cleanup on unmount to prevent issues with route changes
      // The plugin will be cleaned up on next locale change or component unmount
    };
  }, [locale, trackCustomEvent]);

  // Final cleanup on component unmount
  useEffect(() => {
    return () => {
      // Perform final cleanup when component fully unmounts
      cleanupPexipPlugin();
      mountedRef.current = false;
    };
  }, []);

  return (
    <div
      className="pexip-engage-plugin relative min-h-[500px] w-full"
      data-pexip-customer-timezone="Europe/Brussels"
      data-pexip-language={locale === 'fr' ? 'fr-BE' : locale === 'en' ? 'en-GB' : 'nl-BE'}
      key={`skedify-container-${locale}`}
      ref={containerRef}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-b-2 border-t-2 border-gray-900"></div>
          <div>Loading appointment scheduler...</div>
        </div>
      </div>
    </div>
  );
};

export default SkedifyColorAdvice;
